import "./style.scss";
import "components/shared/DetailTabs/style.scss";

import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import { TabContext, TabPanel } from "@mui/lab";
import { Avatar, Box, Tab, ToggleButton, ToggleButtonGroup, Tooltip } from "@mui/material";
import Edit from "assets/img/svg/edit";
import Plus from "assets/img/svg/plus";
import { getPendingImports } from "components/ImportData/utils";
import ModalBox from "components/Modal";
import ArticlesTable from "components/shared/ArticlesTable";
import AssociatedTitles from "components/shared/BeneficiariesTerms";
import ButtonMenu from "components/shared/ButtonMenu";
import Documents from "components/shared/Documents";
import EmptyState from "components/shared/EmptyState";
import TabCountLabel from "components/LoadingDots/TabCountLabel";
import ImagePopup from "components/shared/ImagePopup";
import TextSkeleton from "components/shared/TextSkeleton";
import Heirs from "components/shared/Heirs";
import Representatives from "components/shared/Representatives";
import ResponsiveButton from "components/shared/ResponsiveStyledButton";
import StyledButton from "components/shared/StyledButton";
import { StyledTabs } from "components/shared/StyledTabs";
import Tags from "components/shared/Tags";
import StatementsTab from "components/StatementsTab";
import type { IRepresentative } from "containers/Beneficiars/types";
import { getImportTypeTranslation } from "containers/BulkUpdatesContainer";
import { extractEntityCustomFields, populateGridWithCustomFields } from "containers/CustomFieldsContainer/helpers";
import ScopeContext from "containers/ScopeContext";
import { PortalType } from "contexts/portal.context";
import { formatLocaleDateString, getInitials, maskString } from "helpers/utils";
import useBindingKeyboard from "hooks/useBindingKeyboard";
import useDetailTabSwitch, { getDetailTabPanelContentKey } from "hooks/useDetailTabSwitch";
import { useCompanySettings } from "hooks/useCompanySettings";
import useDetailPreviewLoading from "hooks/useDetailPreviewLoading";
import usePageTitle from "hooks/usePageTitle";
import { usePermissions } from "hooks/usePermissions";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getAllArticlesBeneficiars } from "store/articleStore";
import { fetchAgreements } from "store/agreementsStore";
import {
    deleteBeneficiaryTag,
    fetchAssociatedPersons,
    fetchBeneficiar,
    fetchTermAssociatedToBeneficiaries,
    fetchBeneficiaryStatements,
    fetchHeirs,
    fetchHeirOf,
    fetchTermGroups,
    getBeneficiaryTags,
    markBeneficiaryProfileComplete,
} from "store/beneficiaryStore";
import { fetchPartnerStatements } from "store/partnerStatementsStore";
import { fetchPeriods } from "store/periodStore";
import { useAppDispatch, useAppSelector } from "store/store";
import { fetchBeneficiaryFrameworkContracts } from "store/termTemplatesStore";
import { BeneficiaryVAT, EPortalType } from "store/types";

import variables from "../../assets/styles/_variables.module.scss";
import { Languages } from "../../languages";
import { fetchDocument, fetchDocuments, sendDocuments } from "./../../store/documentStore";
import { fetchDocumentTypes } from "store/beneficiaryDocumentTypesStore";
import BeneficiaryFrameworkContracts from "./BeneficiaryFrameworkContracts";
import GeneralInfoTab from "./GeneralInfoTab";
import GiveAccessToPortal from "./ModalContent/GiveAccessToPortal";
import PaywallAccessToPortal from "./ModalContent/PaywallAccessToPortal";
import { formatSocialProfileType, getAccountingTypologyName, getValueOrDraft, isInvitedAuthor } from "./utils";

interface BeneficiaryProps {
    deleteTerm: (termId: number) => void;
    openEditBeneficiar: () => void;
    editMode?: boolean;
    noBeneficiaries: boolean;
    isListLoading?: boolean;
}

const Beneficiary = ({ openEditBeneficiar, editMode, noBeneficiaries, isListLoading = false }: BeneficiaryProps) => {
    const [t] = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { id } = useParams();
    const location = useLocation();
    const portalType = useContext(PortalType);

    const [avatarDialogType, setAvatarDialogType] = useState(false);
    const [createFrameworkModalOpen, setCreateFrameworkModalOpen] = useState(false);
    const [contractsSegment, setContractsSegment] = useState<"terms" | "framework">(
        location.state?.contractsSegment === "framework" ? "framework" : "terms",
    );

    const { companyInfos, getCompanySettings, countryFeatures } = useCompanySettings();
    const { hasPermissions } = usePermissions();

    const currentAccountingSoftware = getCompanySettings("accounting_software");
    const usesAccountingCodeGroups = currentAccountingSoftware === "pennylane";

    const acceptedPaymentMethods: string[] =
        getCompanySettings("accepted_payment_methods")?.content ?? '["rib","paypal","check"]';
    const usesPortal = getCompanySettings("uses_portal");

    const usesAdvanceCompensation = getCompanySettings("uses_interterm_compensation");
    const { termGroups, statements, statementsStatus } = useAppSelector((state) => state.beneficiary);
    const { jobs } = useAppSelector((state) => state.jobs);
    const { pendingImports } = useAppSelector((state) => state.dataImport);

    const usesMultiPeriod = getCompanySettings("uses_simultaneous_period");
    const usesMultipleCurrencies = getCompanySettings("uses_multiple_currencies");
    const usesStatementTemplates = getCompanySettings("uses_custom_statement_templates");
    const usesBrands = getCompanySettings("uses_multiple_brands");
    const usesBeneficiaryProfileCompleteCheck = getCompanySettings("uses_beneficiary_profile_complete_check");

    usePageTitle(t("Beneficiars.Beneficiars"));

    const { activePeriod: activePeriodId } = useAppSelector((state) => state.period);
    const {
        associatedPersons,
        beneficiar,
        beneficiaryStatus,
        termAssociatedToBeneficiaries,
        termAssociatedToBeneficiariesStatus,
        termAssociatedToBeneficiariesBeneficiaryId,
    } = useAppSelector((state) => state.beneficiary);

    // Using this state is necessary for the collapse within the LastElementTable (see Representatives component):
    const [representativeData, setRepresentativeData] = useState<IRepresentative[]>();

    const { documentList, documentListLoadStatus } = useAppSelector((state) => state.documents);
    const usesDocumentsTracking = !!getCompanySettings("uses_beneficiary_documents_tracking");
    const { documentTypes: allDocumentTypes } = useAppSelector((state) => state.beneficiaryDocumentTypes);
    const activeDocumentTypes = useMemo(
        () => (usesDocumentsTracking ? allDocumentTypes.filter((type) => type.isActive) : undefined),
        [usesDocumentsTracking, allDocumentTypes],
    );

    useEffect(() => {
        if (usesDocumentsTracking && !allDocumentTypes.length) dispatch(fetchDocumentTypes());
    }, [usesDocumentsTracking]);
    const { articles, articleExportStatus } = useAppSelector((state) => state.article);
    const partnerStatements = useAppSelector((state) => state.partnerStatements.partnerStatements);

    const isInvited = isInvitedAuthor([beneficiar.street, beneficiar.siretNumber, beneficiar.socialSecurityNumber]);

    // Fetch custom fields only for beneficiary resource + right entity:
    const { customFields } = useAppSelector((state) => state.customFields);
    const beneficiaryFrameworkContractsCount = useAppSelector(
        (state) => state.termTemplates.beneficiaryFrameworkContracts.length,
    );
    const beneficiaryFrameworkContractsStatus = useAppSelector(
        (state) => state.termTemplates.beneficiaryFrameworkContractsStatus,
    );
    const beneficiaryCustomFields = extractEntityCustomFields(customFields, beneficiar?.entity);
    const currentBeneficiaryCustomFields = useMemo(() => {
        return beneficiar.customFields ?? [];
    }, [beneficiar]);

    const [modalVariant, setModalVariant] = useState<"create" | "edit" | "deleteRep" | "delete" | "access" | null>(
        null,
    );

    // Used to figure out which fields need the masking button.
    const [maskSensitiveInfo, setMaskSensitiveInfo] = useState(() => {
        const metadataBankingFields = countryFeatures.bankingFields.filter((f) => f !== "iban" && f !== "bic");
        return {
            [t("Beneficiars.PhoneNumber")]: true,
            [t("Beneficiars.SecurityNumber")]: true,
            [t("Beneficiars.Siret")]: true,
            [t("Beneficiars.AccountingCode")]: true,
            [t("Beneficiars.IBAN")]: true,
            [t("Beneficiars.Swift")]: true,
            [t("Beneficiars.Paypal")]: true,
            [t("Beneficiars.RoutingNumber")]: true,
            ...Object.fromEntries(metadataBankingFields.map((f) => [t(`Beneficiary.${f}`), true])),
        };
    });

    const { value, tabEnterKey, containerRef, switchTab, handleTabChange } = useDetailTabSwitch(
        location.state?.tab ?? "1",
    );

    const language = Languages().find((lang) => lang.value === (beneficiar.lang ?? "").toLowerCase());

    /**
     * Includes all fields within the Personal Info section of the form:
     */
    const personalInfo = useMemo(() => {
        // Fetch custom fields specific to section.
        const personalCustomFields = beneficiaryCustomFields?.filter(
            (field) => field.formLocation === "personalInfo" && field.valueType !== "longstring",
        );

        const displayedFields: Record<string, string> = {};

        // Default fields to display:
        if (beneficiar) {
            displayedFields[t("Beneficiars.PhoneNumber")] =
                (maskSensitiveInfo[t("Beneficiars.PhoneNumber")] && beneficiar.phoneNumber
                    ? maskString(getValueOrDraft(beneficiar.phoneNumber))
                    : getValueOrDraft(beneficiar.phoneNumber)) || "-";
            displayedFields[t("Beneficiars.Email")] = beneficiar.email || "-";
            displayedFields[t("NewBeneficiary.Language")] = language?.description || "-";

            if (beneficiar.entity === "physical") {
                displayedFields[t("Beneficiars.BirthDate")] = beneficiar.birthDate
                    ? formatLocaleDateString({
                          date: beneficiar.birthDate,
                          options: { numeric: true, timeZone: "UTC" },
                      })
                    : "-";
            }
        }

        // Add custom fields specific to section:
        if (personalCustomFields?.length) {
            populateGridWithCustomFields({
                resourceData: beneficiar,
                resourceCustomFields: personalCustomFields,
                gridObject: displayedFields,
            });
        }

        return displayedFields;
    }, [beneficiaryCustomFields, currentBeneficiaryCustomFields]);

    /**
     * Includes all info about address (no custom fields)
     */
    const addressInfo = beneficiar && {
        [t("NewBeneficiary.StreetNumber")]: getValueOrDraft(beneficiar.street) || "-",
        [t("NewBeneficiary.ZIPCode")]: getValueOrDraft(beneficiar.zipCode) || "-",
        [t("NewBeneficiary.City")]: getValueOrDraft(beneficiar.city) || "-",
        [t("NewBeneficiary.Country")]: beneficiar.country || "-",
    };

    /**
     * Witheld percent is displayed if vat is franchise and the country is different than France (or empty string)
     */
    const withheldPercentApplies =
        beneficiar.vat === BeneficiaryVAT.FRANCHISE &&
        !["", "france"].includes((beneficiar?.country ?? "").toLowerCase());

    /**
     * Includes all info about social and VAT (no custom fields)
     */
    const hasVat = countryFeatures.vat.options.length > 0;
    const hasPrecount = countryFeatures.precount.options.length > 0;

    const profileInfo = beneficiar && {
        ...(hasVat && {
            [t("Beneficiars.VAT")]:
                t(countryFeatures.vat.options.find((o) => o.value === beneficiar.vat)?.label ?? "") || "-",
        }),
        ...(hasPrecount &&
            countryFeatures.precountMode === "direct" && {
                [t("Beneficiars.Precount")]:
                    t(countryFeatures.precount.options.find((o) => o.value === beneficiar.precount)?.label ?? "") ||
                    "-",
            }),
        ...(hasPrecount &&
            countryFeatures.precountMode !== "direct" &&
            !beneficiar.socialProfileType && {
                [t("Beneficiars.Precount")]:
                    formatSocialProfileType({
                        item: beneficiar,
                        isCompanyBelgian: true,
                        distributorType: companyInfos?.distributorType,
                    }) || "-",
            }),
        ...(hasPrecount &&
            countryFeatures.precountMode !== "direct" &&
            beneficiar.socialProfileType && {
                [t("Beneficiars.SocialProfileType")]:
                    formatSocialProfileType({
                        item: beneficiar,
                        isCompanyBelgian: false,
                        distributorType: companyInfos?.distributorType,
                    }) || "-",
            }),
        ...(["physical", "none"].includes(beneficiar.entity ?? "none") && {
            [t("Beneficiars.SecurityNumber")]:
                (maskSensitiveInfo[t("Beneficiars.SecurityNumber")] && beneficiar.socialSecurityNumber
                    ? maskString(getValueOrDraft(beneficiar.socialSecurityNumber))
                    : beneficiar.socialSecurityNumber) || "-",
        }),
        [t("Beneficiars.Siret")]:
            (maskSensitiveInfo[t("Beneficiars.Siret")]
                ? maskString(getValueOrDraft(beneficiar.siretNumber))
                : beneficiar.siretNumber) || "-",
        [t("Beneficiars.Code")]: beneficiar.beneficiaryCode || "-",
        ...(withheldPercentApplies && {
            [t("Beneficiars.WithheldPercent")]: (Math.round(beneficiar.withheldPercent ?? 0) / 100 || "-").toString(),
        }),
        [t("Beneficiars.Activities")]: beneficiar.activityType || "-",
    };

    /**
     * Includes all fields within the Accounting section of the form:
     */
    const accountingInfo = useMemo(() => {
        // Fetch custom fields specific to section.
        const accountingCustomFields = beneficiaryCustomFields?.filter(
            (field) => field.formLocation === "accounting" && field.valueType !== "longstring",
        );

        const displayedFields: Record<string, string> = {};

        // Default fields of section:
        if (beneficiar) {
            displayedFields[t("Beneficiars.Currency")] = beneficiar?.statementCurrency || "-";
            displayedFields[t("Beneficiars.AccountingTypology")] =
                getAccountingTypologyName(beneficiar.accountingTypology ?? "") || "-";

            if (usesAccountingCodeGroups) {
                displayedFields[t("Beneficiars.AccountingCode")] = beneficiar.accountingCodeGroups
                    ? beneficiar.accountingCodeGroups
                          .map((group) => {
                              return group.mainAccount
                                  ? `${group.mainAccount}-${group.auxiliaryAccount}`
                                  : `${group.auxiliaryAccount}`;
                          })
                          .join(", ")
                    : "-";
            } else {
                displayedFields[t("Beneficiars.AccountingCode")] = beneficiar.accountingCode || "-";
            }
        }

        // Add brand only if setting is on:
        if (usesBrands && beneficiar.brandAccountingCode) {
            beneficiar.brandAccountingCode.map((code) => {
                displayedFields[`${t("Beneficiars.BrandAccountingCode", { brandName: code.brand })}`] =
                    code.accountingCode || "-";
            });
        }

        // Add custom fields specific to section.
        if (accountingCustomFields?.length) {
            populateGridWithCustomFields({
                resourceData: beneficiar,
                resourceCustomFields: accountingCustomFields,
                gridObject: displayedFields,
            });
        }

        return displayedFields;
    }, [beneficiaryCustomFields]);

    const beneficiaryProfileInfos = beneficiar && {
        [t("Summary.CreationDate")]: beneficiar.creationDate
            ? formatLocaleDateString({ date: beneficiar.creationDate, options: { numeric: true, timeZone: "UTC" } })
            : "-",
        [t("Titles.LatestEdition")]: beneficiar.lastModifiedDate
            ? formatLocaleDateString({ date: beneficiar.lastModifiedDate, options: { numeric: true, timeZone: "UTC" } })
            : "-",
    };

    /**
     * Includes all fields that are longstring (either native or custom)
     */
    const textAreaFields: { title: string; text: any }[] = useMemo(() => {
        const longStringFields = beneficiaryCustomFields?.filter((field) => field.valueType === "longstring");

        const displayedFields = [] as any;

        if (longStringFields?.length) {
            longStringFields.forEach((item) => {
                const fieldData = beneficiar?.customFields?.find((field) => field.customFieldTypeId === item.id);
                displayedFields.push({ title: item.name, text: fieldData?.value ? fieldData?.value : "-" });
            });
        }

        return displayedFields;
    }, [beneficiaryCustomFields]);

    const handleChange = handleTabChange;

    const handleNavigateTitle = () => {
        if (beneficiar) {
            portalType === "book" &&
                navigate(`/beneficiars/${beneficiar.id}/connect-title`, {
                    state: { beneficiaryId: beneficiar.id, associatedTitles: termAssociatedToBeneficiaries.length },
                });
            ["audiovisual", "press"].includes(portalType) &&
                navigate("/articles/new", {
                    state: {
                        beneficiaryId: beneficiar.id,
                        beneficiaryFirstName: beneficiar.name,
                        beneficiarySurname: beneficiar.surname,
                        beneficiaryName: `${(beneficiar.surname ?? "").toUpperCase()} ${beneficiar.name ?? ""}`.trim(),
                    },
                });
        }
    };

    const handleFileAdd = (file: { formData: FormData }) => {
        dispatch(sendDocuments({ formData: file.formData, beneficiarId: beneficiar!.id }));
    };

    const handleFileDownload = ({ file, beneficiaryId }: { file: any; beneficiaryId: number }) => {
        dispatch(fetchDocument({ file, beneficiaryId }));
    };

    const handleDeleteTag = useCallback(
        (i: number, tagName: string) => {
            dispatch(deleteBeneficiaryTag({ beneficiaryId: +id!, tagId: i, toast: true })).then(async (resp) => {
                if (resp.meta.requestStatus === "fulfilled") {
                    await dispatch(getBeneficiaryTags());
                    // Update beneficiary profile complete checkbox
                    if (usesBeneficiaryProfileCompleteCheck && tagName === t("Beneficiars.ProfileComplete")) {
                        dispatch(
                            markBeneficiaryProfileComplete({
                                beneficiaryId: +id!,
                                isProfileComplete: false,
                                toast: false,
                            }),
                        );
                    }
                }
            });
        },
        [dispatch, id],
    );

    const beneficiaryJobsPending = useMemo(() => {
        return getPendingImports({ jobs, fakeImports: pendingImports, type: ["beneficiar"] }).length > 0;
    }, [pendingImports, jobs]);

    const termJobsPending = useMemo(() => {
        return getPendingImports({ jobs, fakeImports: pendingImports, type: ["terms"] }).length > 0;
    }, [jobs, pendingImports]);

    useEffect(() => {
        !activePeriodId && portalType == EPortalType.BOOK && dispatch(fetchPeriods());
        if (!id || beneficiar?.id !== +id) return;
        if (portalType == "book") {
            dispatch(fetchAssociatedPersons(beneficiar.id)).then((resp) => {
                if (resp.meta.requestStatus === "fulfilled") {
                    setRepresentativeData(resp.payload);
                }
            });
        }
        dispatch(fetchDocuments(beneficiar.id));
    }, [activePeriodId, beneficiar?.id, dispatch, id, portalType]);

    useEffect(() => {
        if (portalType !== "book" || !id || beneficiar?.id !== +id) return;
        const termsListIsCurrent =
            termAssociatedToBeneficiariesBeneficiaryId === beneficiar.id &&
            termAssociatedToBeneficiariesStatus !== "idle";
        if (termsListIsCurrent) return;
        dispatch(fetchTermAssociatedToBeneficiaries({ beneficiaryId: beneficiar.id }));
    }, [
        beneficiar?.id,
        dispatch,
        id,
        portalType,
        termAssociatedToBeneficiariesBeneficiaryId,
        termAssociatedToBeneficiariesStatus,
    ]);

    useEffect(() => {
        if (portalType !== "book" || !id || beneficiar?.id !== +id) return;
        dispatch(fetchHeirs(beneficiar.id));
        dispatch(fetchHeirOf(beneficiar.id));
    }, [beneficiar?.id, dispatch, id, portalType]);

    useEffect(() => {
        if (id) {
            dispatch(fetchBeneficiar(+id));
            if (["audiovisual", "press"].includes(portalType)) {
                dispatch(getAllArticlesBeneficiars(parseInt(id)));
                dispatch(fetchAgreements());
            }
        }
    }, [id]);

    useEffect(() => {
        if (id && usesAdvanceCompensation) {
            dispatch(fetchTermGroups({ beneficiaryId: +id }));
        }

        if (id) {
            dispatch(fetchBeneficiaryStatements({ beneficiaryId: +id }));
            dispatch(fetchBeneficiaryFrameworkContracts({ beneficiaryId: +id }));
        }
    }, [id]);

    useEffect(() => {
        if (usesStatementTemplates && !partnerStatements?.length) dispatch(fetchPartnerStatements());
    }, [dispatch]);

    const activeTabs = {
        book: ["1", "2", "3", "4", "5"],
        press: ["1", "2", "6", "5"],
        audiovisual: ["1", "2", "6", "5"],
    };

    // TODO: can be a hook
    const handleSwitchTabKeyboard = useCallback(
        (ev: KeyboardEvent) => {
            const currentIndex = +activeTabs[portalType].indexOf(value);
            if (ev.code === "ArrowLeft" && currentIndex > 0) switchTab(activeTabs[portalType][currentIndex - 1]);
            else if (ev.code === "ArrowRight" && currentIndex < activeTabs[portalType].length - 1)
                switchTab(activeTabs[portalType][currentIndex + 1]);
            location.state = null;
        },
        [location, portalType, switchTab, value],
    );

    useBindingKeyboard({
        action: handleSwitchTabKeyboard,
        type: "keydown",
        touchToBind: ["ArrowLeft", "ArrowRight"],
    });

    const routeBeneficiaryId = id ? +id : null;
    const { isHeaderLoading, isContentLoading } = useDetailPreviewLoading(
        routeBeneficiaryId,
        beneficiar?.id,
        beneficiaryStatus,
        isListLoading,
    );

    const contractsBelongToBeneficiary = termAssociatedToBeneficiariesBeneficiaryId === beneficiar?.id;
    const hasSettledContracts = contractsBelongToBeneficiary && termAssociatedToBeneficiariesStatus === "succeeded";
    const contractsFailed = contractsBelongToBeneficiary && termAssociatedToBeneficiariesStatus === "failed";
    const isContractsLoading = !hasSettledContracts && !contractsFailed;

    const contractsCount =
        termAssociatedToBeneficiariesStatus === "succeeded"
            ? termAssociatedToBeneficiaries.length
            : beneficiar.titlesCount;
    const hasContracts = termAssociatedToBeneficiaries.length > 0;
    const isContractsCountLoading = isContractsLoading && contractsCount == null;

    if (!isHeaderLoading && (noBeneficiaries || Object.values(beneficiar).length === 0)) {
        // noBeneficiaries for accounts with 0 beneficiaries || When beneficiary deleted
        return (
            <Box
                sx={{
                    display: "flex",
                    width: "100%",
                    height: "100vh",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <EmptyState type={portalType == "book" ? "beneficiary" : "beneficiaryPress"} />
            </Box>
        );
    }

    return (
        <>
            {beneficiar && usesPortal ? (
                <ModalBox
                    open={modalVariant === "access"}
                    onClose={() => setModalVariant(null)}
                    mainTitle={`${t("Beneficiars.GiveAccess")} ${beneficiar.name ?? ""} ${beneficiar.surname}`}
                    noPadding={true}
                >
                    <GiveAccessToPortal
                        close={() => setModalVariant(null)}
                        name={beneficiar.name ?? ""}
                        surname={beneficiar.surname}
                        email={beneficiar.email}
                        beneficiarId={beneficiar.id}
                    />
                </ModalBox>
            ) : (
                <ModalBox
                    open={modalVariant === "access"}
                    onClose={() => setModalVariant(null)}
                    mainTitle={`${t("Beneficiars.BeneficiarPortal")} ${t("Beneficiars.By")} Crealo`}
                >
                    <PaywallAccessToPortal />
                </ModalBox>
            )}
            <div
                ref={containerRef}
                className="beneficiar-container"
                style={
                    editMode
                        ? { overflow: "hidden", position: "relative", height: "100vh", width: "calc(100vw - 585px)" }
                        : {}
                }
            >
                {(beneficiar.id || isHeaderLoading) && (
                    <TabContext value={value}>
                        {beneficiar.id && !isHeaderLoading && (
                            <ImagePopup
                                element={beneficiar}
                                avatarOpen={avatarDialogType}
                                setAvatarOpen={() => setAvatarDialogType(false)}
                            />
                        )}
                        <div>
                            <div className="beneficiar-header">
                                <div className="beneficiar-left-content">
                                    <Avatar
                                        sx={{
                                            width: "80px",
                                            height: "80px",
                                            backgroundColor:
                                                isHeaderLoading || !beneficiar.avatarUrl
                                                    ? variables["beneficiary-avatar-background"]
                                                    : "",
                                            color: variables["beneficiary-avatar-letter"],
                                            fontSize: "18px",
                                            cursor: !isHeaderLoading && beneficiar.avatarUrl !== "" ? "pointer" : "",
                                        }}
                                        variant="circular"
                                        src={isHeaderLoading ? undefined : beneficiar.avatarUrl}
                                        imgProps={{ sx: { objectFit: "cover" } }}
                                        onClick={() =>
                                            !isHeaderLoading && beneficiar.avatarUrl !== "" && setAvatarDialogType(true)
                                        }
                                    >
                                        {!isHeaderLoading &&
                                            !beneficiar.avatarUrl &&
                                            getInitials(`${beneficiar.name} ${beneficiar.surname}`)}
                                    </Avatar>
                                    <div className="beneficiary-info">
                                        <p className="beneficiary-eyebrow">{t("Beneficiars.BeneficiaryLabel")}</p>
                                        <div className="beneficiary-names">
                                            <div className="first-line">
                                                <h1>
                                                    {isHeaderLoading ? (
                                                        <TextSkeleton width="12em" />
                                                    ) : (
                                                        `${beneficiar.surname?.toUpperCase() ?? ""} ${
                                                            beneficiar.name ?? ""
                                                        }`
                                                    )}
                                                </h1>
                                                {!isContentLoading && isInvited && (
                                                    <Tooltip title={t("NewBeneficiary.AuthorInvited")}>
                                                        <div className="invited-icon">
                                                            <MarkEmailReadOutlinedIcon
                                                                sx={{
                                                                    height: "16px",
                                                                    width: "16px",
                                                                    color: "var(--foreground)",
                                                                }}
                                                            />
                                                        </div>
                                                    </Tooltip>
                                                )}
                                                {beneficiar.deceasedAt && (
                                                    <Tooltip
                                                        title={t("Heirs.DeceasedSince", {
                                                            date: formatLocaleDateString({
                                                                date: beneficiar.deceasedAt,
                                                            }),
                                                        })}
                                                    >
                                                        <span className="deceased-chip">{t("Heirs.Deceased")}</span>
                                                    </Tooltip>
                                                )}
                                            </div>
                                            <span>
                                                {isHeaderLoading ? <TextSkeleton width="7em" /> : beneficiar.pseudonym}
                                            </span>
                                        </div>
                                        {isHeaderLoading ? (
                                            <TextSkeleton width="6em" />
                                        ) : (
                                            <Tags
                                                elementId={beneficiar.id}
                                                elementType="beneficiary"
                                                onDelete={handleDeleteTag}
                                                tags={beneficiar.tags}
                                            />
                                        )}
                                    </div>
                                </div>
                                <div className="beneficiar-header-buttons">
                                    <ScopeContext
                                        extraConditions={[!beneficiaryJobsPending, !isContentLoading]}
                                        errorProps={{ disabled: true }}
                                    >
                                        {(props) => {
                                            return (
                                                <ResponsiveButton
                                                    onClick={openEditBeneficiar}
                                                    {...props}
                                                    tooltipText={
                                                        beneficiaryJobsPending
                                                            ? t("AsyncUploads.PendingTooltip", {
                                                                  element: getImportTypeTranslation("beneficiars"),
                                                              })
                                                            : ""
                                                    }
                                                >
                                                    <Edit />
                                                    <span>{t("Beneficiars.EditProfile")}</span>
                                                </ResponsiveButton>
                                            );
                                        }}
                                    </ScopeContext>
                                    <ScopeContext
                                        extraConditions={[
                                            !termJobsPending,
                                            !isContentLoading,
                                            hasPermissions(["article:new"]),
                                        ]}
                                        errorProps={{ disabled: true }}
                                    >
                                        {(scopeProps) => {
                                            const scopeDisabled = Boolean(
                                                (scopeProps as Record<string, unknown> | undefined)?.disabled,
                                            );
                                            const tooltipText = beneficiaryJobsPending
                                                ? t("AsyncUploads.PendingTooltip", {
                                                      element: getImportTypeTranslation("beneficiars"),
                                                  })
                                                : !hasPermissions(["article:new"])
                                                  ? t("LogIn.NotAuthorizedException")
                                                  : "";
                                            if (portalType === "book") {
                                                return (
                                                    <ButtonMenu
                                                        buttonType="primary"
                                                        showExpandBtn={false}
                                                        responsive
                                                        disabled={scopeDisabled}
                                                        tooltipText={tooltipText}
                                                        mainActions={{
                                                            icon: <Plus />,
                                                            name: hasContracts
                                                                ? t("Beneficiars.AddAContract")
                                                                : t("Beneficiars.CreateAContract"),
                                                        }}
                                                        actions={[
                                                            {
                                                                name: t("Beneficiars.CreateContractByTitle"),
                                                                action: () => handleNavigateTitle(),
                                                            },
                                                            {
                                                                name: t("Beneficiars.CreateFrameworkContract"),
                                                                action: () => {
                                                                    switchTab("2");
                                                                    setContractsSegment("framework");
                                                                    setCreateFrameworkModalOpen(true);
                                                                },
                                                            },
                                                        ]}
                                                    />
                                                );
                                            }
                                            return (
                                                <ResponsiveButton
                                                    onClick={() => handleNavigateTitle()}
                                                    buttonType="dark"
                                                    tooltipText={tooltipText}
                                                    {...scopeProps}
                                                >
                                                    <Plus />
                                                    <span>{t(`Beneficiars.CreateAnArticle_${portalType}`)}</span>
                                                </ResponsiveButton>
                                            );
                                        }}
                                    </ScopeContext>
                                </div>
                            </div>
                            <StyledTabs value={value} onChange={handleChange} className="beneficiar-tab">
                                <Tab label={t("Beneficiars.GeneralInformation")} value="1" />
                                {portalType === "book" ? (
                                    <Tab
                                        label={
                                            <TabCountLabel
                                                text={t("Beneficiars.AssociatedTitles")}
                                                count={(contractsCount ?? 0) + beneficiaryFrameworkContractsCount}
                                                isLoading={
                                                    isContractsCountLoading ||
                                                    beneficiaryFrameworkContractsStatus === "pending"
                                                }
                                            />
                                        }
                                        value="2"
                                    />
                                ) : (
                                    <Tab
                                        label={`${t(`Beneficiars.AssociatedArticles_${portalType}`)}${
                                            articles?.length > 0 ? ` (${articles.length})` : ""
                                        }`}
                                        value="2"
                                    />
                                )}
                                {portalType === "book" && <Tab label={`${t("Beneficiars.StatementsTab")}`} value="3" />}
                                {portalType === "book" && (
                                    <Tab
                                        label={`${t("Beneficiars.AssociatedPersons")}${
                                            associatedPersons?.length > 0 ? ` (${associatedPersons?.length})` : ""
                                        }`}
                                        value="4"
                                    />
                                )}
                                {["audiovisual", "press"].includes(portalType) && (
                                    <Tab label={t("Beneficiars.FrameworkContractsTab")} value="6" />
                                )}
                                <Tab label={t("Beneficiars.Documents")} value="5" />
                            </StyledTabs>
                        </div>

                        {/* GENERAL INFO */}
                        <TabPanel value="1" className="detail-tab-panel-root">
                            <div
                                className="detail-tab-panel-content"
                                key={getDetailTabPanelContentKey("1", value, tabEnterKey)}
                            >
                                <div className="detail-tab-body">
                                    <GeneralInfoTab
                                        usesBeneficiaryProfileCompleteCheck={usesBeneficiaryProfileCompleteCheck}
                                        beneficiar={beneficiar}
                                        isLoading={isContentLoading}
                                        data={{
                                            personalInfo,
                                            addressInfo,
                                            profileInfo,
                                            accountingInfo,
                                            beneficiaryProfileInfos,
                                        }}
                                        textAreaFields={textAreaFields}
                                        maskSensitiveInfo={maskSensitiveInfo}
                                        setMaskSensitiveInfo={setMaskSensitiveInfo}
                                        setModalVariant={setModalVariant}
                                    />
                                </div>
                            </div>
                        </TabPanel>

                        {/* CONTRACTS */}
                        <TabPanel value="2" className="detail-tab-panel-root">
                            <div
                                className="detail-tab-panel-content"
                                key={getDetailTabPanelContentKey("2", value, tabEnterKey)}
                            >
                                <div className="detail-tab-body">
                                    {portalType === "book" ? (
                                        <>
                                            <ToggleButtonGroup
                                                value={contractsSegment}
                                                exclusive
                                                onChange={(_, newValue) => {
                                                    if (newValue) setContractsSegment(newValue);
                                                }}
                                                size="small"
                                                sx={{
                                                    marginBottom: "16px",
                                                    "& .MuiToggleButton-root": {
                                                        textTransform: "none",
                                                        fontSize: "12.5px",
                                                        fontWeight: 500,
                                                        padding: "4px 14px",
                                                        border: "1px solid #E0DEE0",
                                                        color: "#4D5C78",
                                                    },
                                                    "& .MuiToggleButton-root.Mui-selected": {
                                                        background: "#0E0E10",
                                                        color: "#fff",
                                                        "&:hover": { background: "#0E0E10" },
                                                    },
                                                }}
                                            >
                                                <ToggleButton value="terms">
                                                    {t("Beneficiars.ContractsSegmentTerms")}
                                                    {hasContracts && ` (${termAssociatedToBeneficiaries.length})`}
                                                </ToggleButton>
                                                <ToggleButton value="framework">
                                                    {t("Beneficiars.ContractsSegmentFramework")} (
                                                    {beneficiaryFrameworkContractsCount})
                                                </ToggleButton>
                                            </ToggleButtonGroup>

                                            {contractsSegment === "terms" ? (
                                                isContractsLoading || hasContracts ? (
                                                    <>
                                                        <AssociatedTitles
                                                            title=""
                                                            currentBeneficiarName={`${beneficiar.surname ?? ""} ${
                                                                beneficiar.name ?? ""
                                                            }`}
                                                            currentBeneficiarId={beneficiar.id}
                                                            currentBeneficiary={beneficiar}
                                                            company={companyInfos}
                                                            usesAdvanceCompensation={usesAdvanceCompensation}
                                                            termGroups={termGroups}
                                                            usesMultiPeriod={usesMultiPeriod}
                                                            isLoading={isContractsLoading}
                                                        />
                                                        {!isContractsLoading && (
                                                            <StyledButton
                                                                sx={{
                                                                    gap: "8px",
                                                                    padding: "4px 16px",
                                                                    background: "white",
                                                                    color: "#E0DEE0",
                                                                    width: "100%",
                                                                }}
                                                                onClick={() => handleNavigateTitle()}
                                                                buttonType="secondary"
                                                            >
                                                                <Plus />
                                                                {hasContracts
                                                                    ? t("Beneficiars.AddAContract")
                                                                    : t("Beneficiars.CreateAContract")}
                                                            </StyledButton>
                                                        )}
                                                    </>
                                                ) : (
                                                    <>
                                                        <EmptyState type="generic" sx={{ margin: "56px auto 0" }} />
                                                        <StyledButton
                                                            sx={{
                                                                gap: "8px",
                                                                padding: "4px 16px",
                                                                background: "white",
                                                                color: "#E0DEE0",
                                                                margin: "auto",
                                                                display: "flex",
                                                            }}
                                                            onClick={() => handleNavigateTitle()}
                                                            buttonType="secondary"
                                                        >
                                                            <Plus />
                                                            {t("Beneficiars.CreateAContract")}
                                                        </StyledButton>
                                                    </>
                                                )
                                            ) : (
                                                <BeneficiaryFrameworkContracts
                                                    beneficiaryId={beneficiar.id}
                                                    beneficiaryFullName={`${beneficiar.surname ?? ""} ${
                                                        beneficiar.name ?? ""
                                                    }`}
                                                    beneficiaryEmail={beneficiar.email}
                                                    createModalOpen={createFrameworkModalOpen}
                                                    onCreateModalOpenChange={setCreateFrameworkModalOpen}
                                                />
                                            )}
                                        </>
                                    ) : (
                                        articles &&
                                        articles.length >= 0 && (
                                            <ArticlesTable
                                                associatedArticles={articles}
                                                beneficiary={beneficiar}
                                                articleExportStatus={articleExportStatus}
                                                pageType="beneficiary"
                                            />
                                        )
                                    )}
                                </div>
                            </div>
                        </TabPanel>

                        {/* STATEMENT */}
                        <TabPanel value="3" className="detail-tab-panel-root">
                            <div
                                className="detail-tab-panel-content"
                                key={getDetailTabPanelContentKey("3", value, tabEnterKey)}
                            >
                                <div className="detail-tab-body">
                                    <StatementsTab
                                        beneficiary={beneficiar}
                                        statements={statements}
                                        statementsStatus={statementsStatus}
                                        companyCurrency={companyInfos?.currency ?? "EUR"}
                                        usesMultipleCurrencies={usesMultipleCurrencies}
                                        activePeriod={activePeriodId}
                                    />
                                </div>
                            </div>
                        </TabPanel>

                        {/* REPRESENTATIVE */}
                        <TabPanel value="4" className="detail-tab-panel-root">
                            <div
                                className="detail-tab-panel-content"
                                key={getDetailTabPanelContentKey("4", value, tabEnterKey)}
                            >
                                <div className="detail-tab-body">
                                    <Representatives
                                        representatives={representativeData}
                                        beneficiary={beneficiar}
                                        setRepresentativeData={setRepresentativeData}
                                    />
                                    <Heirs beneficiary={beneficiar} />
                                </div>
                            </div>
                        </TabPanel>

                        {/* DOCUMENTS */}
                        <TabPanel value="5" className="detail-tab-panel-root">
                            <div
                                className="detail-tab-panel-content"
                                key={getDetailTabPanelContentKey("5", value, tabEnterKey)}
                            >
                                <div className="detail-tab-body">
                                    <Documents
                                        documentList={documentList}
                                        documentStatus={documentListLoadStatus}
                                        handleFileAdd={handleFileAdd}
                                        handleFileDownload={handleFileDownload}
                                        hasSubtitles={false}
                                        documentTypes={activeDocumentTypes}
                                        beneficiaryId={beneficiar?.id}
                                    />
                                </div>
                            </div>
                        </TabPanel>

                        {/* FRAMEWORK CONTRACTS (press/audiovisual) */}
                        {["audiovisual", "press"].includes(portalType) && (
                            <TabPanel value="6">
                                <BeneficiaryFrameworkContracts
                                    beneficiaryId={beneficiar.id}
                                    beneficiaryFullName={`${beneficiar.surname ?? ""} ${beneficiar.name ?? ""}`}
                                    beneficiaryEmail={beneficiar.email}
                                />
                            </TabPanel>
                        )}
                    </TabContext>
                )}
                {!beneficiar.id && (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh" }}>
                        <EmptyState type="generic" />
                    </div>
                )}
            </div>
        </>
    );
};

export default Beneficiary;
